import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { AuthApiClient } from '../../services';
import { authSessionEvents } from '../auth-session';
import { registerEvents } from './register.events';

/**
 * Signal store for managing the registration flow.
 *
 * Handles new user registration requests.
 *
 * @example
 * const register = inject(RegisterStore);
 * const dispatch = injectDispatch(registerEvents);
 *
 * // In a component
 * dispatch.registerRequested({
 *   email: 'newuser@example.com',
 *   password: 'secret',
 *   name: 'John Doe'
 * });
 *
 * // Watch registration state
 * effect(() => {
 *   if (register.isSuccess() === 'success') {
 *     // Handle successful registration
 *   }
 * });
 */
export const RegisterStore = signalStore(
  withRequestStatus(),
  withReducer(
    on(registerEvents.registerRequested, () => ({
      requestStatus: 'loading',
    })),
    on(registerEvents.registerSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(registerEvents.registerFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(
      authSessionEvents.userCompanyFailed,
      authSessionEvents.userDataFailed,
      () => ({
        requestStatus: 'initial',
      })
    )
  ),
  withEventHandlers(
    (_, events = inject(Events), authService = inject(AuthApiClient)) => ({
      register$: events.on(registerEvents.registerRequested).pipe(
        switchMap(({ payload }) =>
          authService.register(payload).pipe(
            mapResponse({
              next: (registerResponse) =>
                registerEvents.registerSucceeded({
                  accessToken: registerResponse.token,
                }),
              error: (error: HttpErrorResponse) =>
                registerEvents.registerFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
    })
  )
);
